import React, { createContext, useContext, useState, useMemo } from 'react';
import { RecurringEvent, User } from '../pages/Calendar/types';
import { Event } from '@pv/common/graphql/types';

interface EventsContextType {
  events: Event[];
  recurringEvents: RecurringEvent[];
  setEvents: (events: Event[]) => void;
  setRecurringEvents: (recurringEvents: RecurringEvent[]) => void;
  owners: User[];
  setOwners: (owners: User[]) => void;
}

const EventsContext = createContext<EventsContextType>({
  events: [],
  recurringEvents: [],
  setEvents: () => {},
  setRecurringEvents: () => {},
  owners: [],
  setOwners: () => {},
});

export const useEvents = () => useContext(EventsContext);

export const EventsProvider = ({ children }: { children: React.ReactNode }) => {
  const [events, setEvents] = useState<Event[]>([]);
  const [owners, setOwners] = useState<User[]>([]);
  const [recurringEvents, setRecurringEvents] = useState<RecurringEvent[]>([]);
  const memoizedValue = useMemo(
    () => ({
      events,
      recurringEvents,
      setEvents,
      setRecurringEvents,
      owners,
      setOwners,
    }),
    [events, recurringEvents, owners]
  );

  React.useEffect(() => {
    if (events.length === 0) {
      return;
    }
    const eventOwnersById = events.reduce((acc, event) => {
      if (event.user?.id) {
        acc[event.user.id] = event.user;
      }
      return acc;
    }, {} as Record<string, User>);
    const eventOwners = Object.values(eventOwnersById);
    setOwners(eventOwners);
  }, [events]);

  return (
    <EventsContext.Provider value={memoizedValue}>
      {children}
    </EventsContext.Provider>
  );
};

export default EventsProvider;
